import { Component, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    createBlock,
    linkTextField,
    headingField,
    htmlField,
    buttonField,
    arrayField,
    textField,
    descriptiveImageField,
} from "./blockBuilder";

import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import {
    SimpleCentered,
    SplitWithImageLeft,
    SplitWithImageRight,
    SplitWithScreenshot,
    WithAlignedImageLeft,
    WithAlignedImageRight,
    WithAppScreenshot,
    WithOffsetImage,
} from "@/views/blocks/hero/hero";
import type { HeroBaseProps } from "@/views/blocks/hero/hero.interfaces";
import participantImageSrc1 from "@/views/blocks/hero/shared/socialproof/images/ellipse1.png";
import participantImageSrc2 from "@/views/blocks/hero/shared/socialproof/images/ellipse2.png";
import participantImageSrc3 from "@/views/blocks/hero/shared/socialproof/images/ellipse3.png";
import participantImageSrc4 from "@/views/blocks/hero/shared/socialproof/images/ellipse4.png";
import participantImageSrc5 from "@/views/blocks/hero/shared/socialproof/images/ellipse5.png";
import heroImage from "@/views/blocks/hero/shared/images/image.jpg";
import screenshotImage from "@/views/blocks/hero/shared/images/page-left.png";
import appScreenshotImage from "@/views/blocks/hero/shared/images/page-top.png";

export function useHeros() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();

    function createBaseHeroBlock({
        component,
        typeId,
        name,
        extendBlock,
    }: {
        component: Component;
        typeId: string;
        name: string;
        extendBlock?: (block: ReturnType<typeof createBlock>) => ReturnType<typeof createBlock>;
    }): BlockDef {
        let block = createBlock<Omit<HeroBaseProps, "image">>({
            typeId,
            type: "hero",
            name: t(name),
            component,
        })
            .withField(
                "socialProofText",
                textField("Social proof text", "9000+ Participants in 2 years")
            )
            .withArrayField(
                "socialProofImages",
                arrayField(
                    "Image",
                    "socialProofImages",
                    {
                        src: { label: "Image", type: "image" },
                        alt: { label: "Alt", type: "text" },
                    },
                    [
                        { src: participantImageSrc1, alt: "Participant 1" },
                        { src: participantImageSrc2, alt: "Participant 2" },
                        { src: participantImageSrc3, alt: "Participant 3" },
                        { src: participantImageSrc4, alt: "Participant 4" },
                        { src: participantImageSrc5, alt: "Participant 5" },
                    ]
                )
            )
            .withField(
                "tag",
                linkTextField("Tag", {
                    text: "Easytools quick start",
                    label: "Read more",
                    href: "https://www.easy.tools/changelog/easytools-quick-start",
                })
            )
            .withField("title", headingField("Title", "Transform Business with Easytools", "h1"))
            .withField(
                "paragraph",
                htmlField(
                    "Paragraph",
                    "Streamline your workflow, boost productivity, and make your business operations more efficient with our comprehensive suite of tools designed for modern enterprises."
                )
            )
            .withArrayField(
                "details",
                arrayField(
                    "Details",
                    "details",
                    {
                        title: { label: "Title" },
                        description: { label: "Description" },
                    },
                    [
                        {
                            title: "24.05",
                            description: "Start",
                        },
                        {
                            title: "4500+",
                            description: "Graduates",
                        },
                        {
                            title: "6+1",
                            description: "Weeks",
                        },
                    ]
                )
            )
            .withField(
                "primaryButton",
                buttonField("Primary button", {
                    label: "Get started",
                    href: "https://cart.easy.tools/register",
                })
            )
            .withField(
                "secondaryButton",
                buttonField("Secondary button", {
                    label: "Learn more",
                    href: "https://www.easy.tools/changelog/easytools-quick-start",
                })
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ]);

        if (extendBlock) {
            block = extendBlock(block);
        }

        return block.build();
    }

    const simpleCenteredBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "SimpleCentered",
            name: t("page.blocks.simpleCentered"),
            component: SimpleCentered,
        })
    );

    const splitWithImageLeftBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            component: SplitWithImageLeft,
            typeId: "SplitWithImageLeft",
            name: t("page.blocks.splitWithImageLeft"),
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField("Image", { src: heroImage, alt: "Hero image" })
                ),
        })
    );

    const splitWithImageRightBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            component: SplitWithImageRight,
            typeId: "SplitWithImageRight",
            name: t("page.blocks.splitWithImageRight"),
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField("Image", { src: heroImage, alt: "Hero image" })
                ),
        })
    );

    const splitWithScreenshotBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "SplitWithScreenshot",
            name: t("page.blocks.splitWithScreenshot"),
            component: SplitWithScreenshot,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField("Image", { src: screenshotImage, alt: "Hero image" })
                ),
        })
    );

    const withAppScreenshotBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAppScreenshot",
            name: t("page.blocks.withAppScreenshot"),
            component: WithAppScreenshot,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField("Image", { src: appScreenshotImage, alt: "Hero image" })
                ),
        })
    );

    const withAlignedImageLeftBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAlignedImageLeft",
            name: t("page.blocks.withAlignedImageLeft"),
            component: WithAlignedImageLeft,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField("Image", { src: heroImage, alt: "Hero image" })
                ),
        })
    );

    const withAlignedImageRightBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithAlignedImageRight",
            name: t("page.blocks.withAlignedImageRight"),
            component: WithAlignedImageRight,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField("Image", { src: heroImage, alt: "Hero image" })
                ),
        })
    );

    const withOffsetImageBlock = computed<BlockDef>(() =>
        createBaseHeroBlock({
            typeId: "WithOffsetImage",
            name: t("page.blocks.withOffsetImage"),
            component: WithOffsetImage,
            extendBlock: (block) =>
                block.withField(
                    "image",
                    descriptiveImageField("Image", { src: heroImage, alt: "Hero image" })
                ),
        })
    );

    return {
        blocks: computed(() => [
            simpleCenteredBlock.value,
            splitWithImageLeftBlock.value,
            splitWithImageRightBlock.value,
            splitWithScreenshotBlock.value,
            withAppScreenshotBlock.value,
            withAlignedImageLeftBlock.value,
            withAlignedImageRightBlock.value,
            withOffsetImageBlock.value,
        ]),
    };
}
